import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { authEndpoints } from '@/lib/api/endpoints';
import { clearSession } from '@/hooks/useAuth';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

const inviteSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required').max(100, 'First name is too long'),
    lastName: z.string().min(1, 'Last name is required').max(100, 'Last name is too long'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password is too long')
      .regex(/[a-z]/, 'Password must contain a lowercase letter')
      .regex(/[A-Z]/, 'Password must contain an uppercase letter')
      .regex(/\d/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain a special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type InviteFormValues = z.infer<typeof inviteSchema>;

interface InviteInfo {
  inviteId: string;
  tenantId: string;
  companyId: string;
  companyName: string;
  email: string;
  expiresAt: string;
  roleIds: string[];
}

interface AcceptInviteResponse {
  inviteId: string;
  membershipId: string;
  tenantId: string;
  companyId: string;
  userId: string;
}

export const Route = createFileRoute('/(public)/invite/$token')({
  component: InvitePage,
});

function InvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();

  const inviteInfoQuery = useApiQuery<InviteInfo>({
    queryKey: ['invite-info', token],
    queryFn: () => apiGet<InviteInfo>(operationsClient, authEndpoints.inviteContext(token)),
    showErrorToast: false,
    retry: false,
  });

  const acceptInviteMutation = useApiMutation<
    AcceptInviteResponse,
    { firstName: string; lastName: string; password: string }
  >({
    mutationFn: (data) =>
      apiPost<AcceptInviteResponse, typeof data>(
        operationsClient,
        authEndpoints.acceptInvite(token),
        data,
      ),
    showErrorToast: false,
  });

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { firstName: '', lastName: '', password: '', confirmPassword: '' },
  });

  async function onSubmit(values: InviteFormValues) {
    form.clearErrors('root');
    try {
      await acceptInviteMutation.mutateAsync({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        password: values.password,
      });
      // Scrub any prior session on this (possibly shared) device before handing
      // the browser to the login form — clearSession() is the single chokepoint
      // that also drops the react-query cache, not just the auth token. Write
      // the invited-email hint AFTER the scrub so the login form can prefill it.
      clearSession();
      const email = inviteInfoQuery.data?.email;
      if (email) {
        localStorage.setItem('notifi:last-email', email);
      }
      toast.success('Account created. Please sign in.');
      void navigate({ to: '/login' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      form.setError('root', { message });
    }
  }

  if (inviteInfoQuery.isPending) {
    return (
      <PublicCenteredPage>
        <Loader2 className="h-8 w-8 animate-spin" />
      </PublicCenteredPage>
    );
  }

  if (inviteInfoQuery.isError) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-md p-6 text-center">
          <CardHeader>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>This invitation link is invalid or has expired.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              {/* from=reset makes the login route's beforeLoad scrub any
                  lingering session, so an expired invite opened on a shared
                  device can't bounce the next person into the prior user's
                  dashboard. */}
              <Link to="/login" search={{ from: 'reset' }}>
                Back to Login
              </Link>
            </Button>
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }

  const invitedEmail = inviteInfoQuery.data?.email;
  const companyName = inviteInfoQuery.data?.companyName;

  return (
    <PublicCenteredPage>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Accept invitation</CardTitle>
          <CardDescription>
            {companyName
              ? `Create your account for ${companyName}`
              : 'Create your account to accept this invite'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitedEmail ? (
            <div className="mb-4 rounded-md border bg-muted/30 p-3 text-sm">
              <p className="text-muted-foreground">Invited email</p>
              <p className="truncate font-medium">{invitedEmail}</p>
            </div>
          ) : null}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input autoComplete="given-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input autoComplete="family-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root && (
                <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
              )}

              <Button type="submit" className="w-full" disabled={acceptInviteMutation.isPending}>
                {acceptInviteMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Activating...
                  </>
                ) : (
                  'Activate account'
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
